/**
 * GST Data Transformer
 * Converts LogiSlip invoice format to GSTR-1 and GSTR-3B formats required by IRIS GST API
 */

import { getStateCodeFromGSTIN, isInterstate } from './gstValidators';

/**
 * Format date to DD-MM-YYYY for IRIS API
 * @param {string} dateString - Date in any valid format
 * @returns {string} - Date in DD-MM-YYYY format
 */
const formatDateForIRIS = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Extract tax details from invoice taxes array
 * @param {Array} taxes - Array of tax objects
 * @returns {Object} - { cgst, sgst, igst, cess }
 */
const extractTaxDetails = (taxes) => {
  const taxDetails = {
    cgst: 0,
    sgst: 0,
    igst: 0,
    cess: 0
  };

  if (!taxes || !Array.isArray(taxes)) {
    return taxDetails;
  }

  taxes.forEach(tax => {
    const title = (tax.title || '').toLowerCase();
    const amount = parseFloat(tax.amount) || 0;

    if (title.includes('cgst')) {
      taxDetails.cgst += amount;
    } else if (title.includes('sgst')) {
      taxDetails.sgst += amount;
    } else if (title.includes('igst')) {
      taxDetails.igst += amount;
    } else if (title.includes('cess')) {
      taxDetails.cess += amount;
    }
  });

  return taxDetails;
};

/**
 * Calculate tax rate from tax amounts and taxable value
 * @param {Object} taxDetails - Tax details object
 * @param {number} taxableValue - Taxable value
 * @returns {number} - Tax rate percentage
 */
const calculateTaxRate = (taxDetails, taxableValue) => {
  if (!taxableValue || taxableValue === 0) return 0;
  
  const totalTax = taxDetails.cgst + taxDetails.sgst + taxDetails.igst;
  const rate = (totalTax / taxableValue) * 100;
  
  // Round to nearest standard GST rate (0, 5, 12, 18, 28)
  const standardRates = [0, 0.25, 3, 5, 12, 18, 28];
  return standardRates.reduce((prev, curr) => 
    Math.abs(curr - rate) < Math.abs(prev - rate) ? curr : prev
  );
};

/**
 * Calculate taxable value from total amount and taxes
 * @param {number} totalAmount - Total invoice amount
 * @param {Object} taxDetails - Tax details object
 * @returns {number} - Taxable value
 */
const calculateTaxableValue = (totalAmount, taxDetails) => {
  const totalTax = taxDetails.cgst + taxDetails.sgst + taxDetails.igst + taxDetails.cess;
  return parseFloat((totalAmount - totalTax).toFixed(2));
};

/**
 * Transform single LogiSlip invoice to GSTR-1 B2B invoice format
 * @param {Object} invoice - LogiSlip invoice object
 * @param {string} companyGSTIN - Company GSTIN
 * @returns {Object} - GSTR-1 B2B invoice format
 */
const transformInvoiceToGSTR1Format = (invoice, companyGSTIN) => {
  const taxDetails = extractTaxDetails(invoice.taxes);
  const totalAmount = parseFloat(invoice.totalAmount) || 0;
  const taxableValue = calculateTaxableValue(totalAmount, taxDetails);
  const taxRate = calculateTaxRate(taxDetails, taxableValue);
  
  const clientGSTIN = invoice.clientDetail?.GST;
  const placeOfSupply = clientGSTIN ? getStateCodeFromGSTIN(clientGSTIN) : getStateCodeFromGSTIN(companyGSTIN);
  const interstate = clientGSTIN ? isInterstate(companyGSTIN, clientGSTIN) : false;

  return {
    inum: invoice.invoiceNo,
    idt: formatDateForIRIS(invoice.createdDate),
    val: parseFloat(totalAmount.toFixed(2)),
    pos: placeOfSupply,
    rchrg: 'N', // Reverse charge - N for normal transactions
    inv_typ: 'R', // R = Regular, SEZWP = SEZ with payment, SEZWOP = SEZ without payment
    itms: [{
      num: 1, // Item serial number
      itm_det: {
        txval: taxableValue, // Taxable value
        rt: taxRate, // Tax rate
        csamt: taxDetails.cess, // Cess amount
        camt: interstate ? 0 : taxDetails.cgst, // CGST amount (0 for interstate)
        samt: interstate ? 0 : taxDetails.sgst, // SGST amount (0 for interstate)
        iamt: interstate ? taxDetails.igst : 0 // IGST amount (0 for intrastate)
      }
    }]
  };
};

/**
 * Group invoices by client GSTIN for B2B section
 * @param {Array} invoices - Array of invoices
 * @param {string} companyGSTIN - Company GSTIN
 * @returns {Array} - Array of B2B entries grouped by GSTIN
 */
const groupInvoicesByGSTIN = (invoices, companyGSTIN) => {
  const b2bMap = new Map();
  const b2cInvoices = [];

  invoices.forEach(invoice => {
    const clientGSTIN = invoice.clientDetail?.GST;
    
    if (clientGSTIN && clientGSTIN.trim()) {
      // B2B transaction
      const transformedInvoice = transformInvoiceToGSTR1Format(invoice, companyGSTIN);
      
      if (b2bMap.has(clientGSTIN)) {
        b2bMap.get(clientGSTIN).inv.push(transformedInvoice);
      } else {
        b2bMap.set(clientGSTIN, {
          ctin: clientGSTIN,
          inv: [transformedInvoice]
        });
      }
    } else {
      // B2C transaction - handle separately
      b2cInvoices.push(invoice);
    }
  });

  return {
    b2b: Array.from(b2bMap.values()),
    b2c: b2cInvoices
  };
};

/**
 * Transform LogiSlip invoices to GSTR-1 format
 * @param {Array} invoices - Array of LogiSlip invoices
 * @param {string} companyGSTIN - Company GSTIN
 * @param {string} returnPeriod - Return period in MMYYYY format
 * @returns {Object} - Complete GSTR-1 data for IRIS API
 */
export const transformToGSTR1 = (invoices, companyGSTIN, returnPeriod) => {
  const { b2b, b2c } = groupInvoicesByGSTIN(invoices, companyGSTIN);

  const gstr1Data = {
    gstin: companyGSTIN,
    return_period: returnPeriod,
    b2b: b2b
  };

  // Add B2C section if there are B2C invoices
  if (b2c.length > 0) {
    gstr1Data.b2cs = transformB2CInvoices(b2c, companyGSTIN);
  }

  return gstr1Data;
};

/**
 * Transform B2C invoices to GSTR-1 B2C format
 * B2C invoices are aggregated by state, tax rate, and invoice type
 * @param {Array} b2cInvoices - Array of B2C invoices
 * @param {string} companyGSTIN - Company GSTIN
 * @returns {Array} - B2C section data
 */
const transformB2CInvoices = (b2cInvoices, companyGSTIN) => {
  const b2cMap = new Map();
  const companyState = getStateCodeFromGSTIN(companyGSTIN);

  b2cInvoices.forEach(invoice => {
    const taxDetails = extractTaxDetails(invoice.taxes);
    const totalAmount = parseFloat(invoice.totalAmount) || 0;
    const taxableValue = calculateTaxableValue(totalAmount, taxDetails);
    const taxRate = calculateTaxRate(taxDetails, taxableValue);
    
    // For B2C, we assume intrastate unless specified otherwise
    const pos = companyState;
    const key = `${pos}_${taxRate}`;

    if (b2cMap.has(key)) {
      const existing = b2cMap.get(key);
      existing.txval += taxableValue;
      existing.camt += taxDetails.cgst;
      existing.samt += taxDetails.sgst;
      existing.iamt += taxDetails.igst;
      existing.csamt += taxDetails.cess;
    } else {
      b2cMap.set(key, {
        pos: pos,
        sply_ty: 'INTRA', // INTRA or INTER
        txval: taxableValue,
        rt: taxRate,
        camt: taxDetails.cgst,
        samt: taxDetails.sgst,
        iamt: taxDetails.igst,
        csamt: taxDetails.cess
      });
    }
  });

  return Array.from(b2cMap.values()).map(entry => ({
    ...entry,
    txval: parseFloat(entry.txval.toFixed(2)),
    camt: parseFloat(entry.camt.toFixed(2)),
    samt: parseFloat(entry.samt.toFixed(2)),
    iamt: parseFloat(entry.iamt.toFixed(2)),
    csamt: parseFloat(entry.csamt.toFixed(2))
  }));
};

/**
 * Transform invoices to GSTR-3B format
 * GSTR-3B is a monthly summary return
 * @param {Array} invoices - Array of LogiSlip invoices
 * @param {string} companyGSTIN - Company GSTIN
 * @param {string} returnPeriod - Return period in MMYYYY format
 * @returns {Object} - Complete GSTR-3B data for IRIS API
 */
export const transformToGSTR3B = (invoices, companyGSTIN, returnPeriod) => {
  let totalTaxableValue = 0;
  let totalCGST = 0;
  let totalSGST = 0;
  let totalIGST = 0;
  let totalCess = 0;

  invoices.forEach(invoice => {
    const taxDetails = extractTaxDetails(invoice.taxes);
    const totalAmount = parseFloat(invoice.totalAmount) || 0;
    const taxableValue = calculateTaxableValue(totalAmount, taxDetails);

    totalTaxableValue += taxableValue;
    totalCGST += taxDetails.cgst;
    totalSGST += taxDetails.sgst;
    totalIGST += taxDetails.igst;
    totalCess += taxDetails.cess;
  });

  const gstr3bData = {
    gstin: companyGSTIN,
    return_period: returnPeriod,
    // Section 3.1 - Outward taxable supplies
    sup_details: {
      osup_det: {
        txval: parseFloat(totalTaxableValue.toFixed(2)),
        iamt: parseFloat(totalIGST.toFixed(2)),
        camt: parseFloat(totalCGST.toFixed(2)),
        samt: parseFloat(totalSGST.toFixed(2)),
        csamt: parseFloat(totalCess.toFixed(2))
      }
    },
    // Section 3.2 - Inter-state supplies (informational)
    inter_sup: {
      txval: parseFloat(totalTaxableValue.toFixed(2)),
      iamt: parseFloat(totalIGST.toFixed(2))
    },
    // Section 5.1 - Total tax liability
    itc_elg: {
      itc_avl: [{
        ty: 'INPUTS',
        iamt: 0,
        camt: 0,
        samt: 0,
        csamt: 0
      }]
    }
  };

  return gstr3bData;
};

/**
 * Calculate summary statistics from invoices
 * @param {Array} invoices - Array of invoices
 * @returns {Object} - Summary statistics
 */
export const calculateGSTSummary = (invoices) => {
  let totalInvoices = invoices.length;
  let totalAmount = 0;
  let totalTaxableValue = 0;
  let totalCGST = 0;
  let totalSGST = 0;
  let totalIGST = 0;
  let totalCess = 0;
  let b2bCount = 0;
  let b2cCount = 0;

  invoices.forEach(invoice => {
    const taxDetails = extractTaxDetails(invoice.taxes);
    const amount = parseFloat(invoice.totalAmount) || 0;
    const taxableValue = calculateTaxableValue(amount, taxDetails);

    totalAmount += amount;
    totalTaxableValue += taxableValue;
    totalCGST += taxDetails.cgst;
    totalSGST += taxDetails.sgst;
    totalIGST += taxDetails.igst;
    totalCess += taxDetails.cess;

    const clientGSTIN = invoice.clientDetail?.GST;
    if (clientGSTIN && clientGSTIN.trim()) {
      b2bCount++;
    } else {
      b2cCount++;
    }
  });

  return {
    totalInvoices,
    totalAmount: parseFloat(totalAmount.toFixed(2)),
    totalTaxableValue: parseFloat(totalTaxableValue.toFixed(2)),
    totalCGST: parseFloat(totalCGST.toFixed(2)),
    totalSGST: parseFloat(totalSGST.toFixed(2)),
    totalIGST: parseFloat(totalIGST.toFixed(2)),
    totalCess: parseFloat(totalCess.toFixed(2)),
    totalTax: parseFloat((totalCGST + totalSGST + totalIGST + totalCess).toFixed(2)),
    b2bCount,
    b2cCount
  };
};

/**
 * Generate return period from date range
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {string} - Return period in MMYYYY format
 */
export const generateReturnPeriod = (startDate, endDate) => {
  // Use the month from the end date
  const date = new Date(endDate);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}${year}`;
};

const gstTransformerUtils = {
  transformToGSTR1,
  transformToGSTR3B,
  calculateGSTSummary,
  generateReturnPeriod
};

export default gstTransformerUtils;


